import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from '@tanstack/react-query';
import { Toast } from 'primereact/toast';
import { useRef } from 'react';
import {
  endpointPortfolioCompanyByIdGET,
  endpointPortfolioCompanyPUT,
  portfolioCompanyPutPayloadSchema,
} from '~/api/def/endpoints/portfolioCompanyEndpoint';
import {
  EditPortfolioCompany,
  EditPortfolioCompanyValues,
} from '~/components/forms/EditPortfolioCompany/EditPortfolioCompany';

type OperationSectionProps = {
  portfolioCompanyId: string;
};

export default function OperationSection({
  portfolioCompanyId,
}: OperationSectionProps) {
  const queryClient = useQueryClient();
  const toast = useRef<Toast>(null);

  const { data: selectedPortfolioCompanyDate, refetch: refetchPcDate } =
    useSuspenseQuery({
      queryKey: ['portfolioCompanyGet', portfolioCompanyId],
      queryFn: () => endpointPortfolioCompanyByIdGET(portfolioCompanyId),
    });

  const mutation = useMutation({
    mutationFn: (data: EditPortfolioCompanyValues) => {
      return endpointPortfolioCompanyPUT(portfolioCompanyId, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['portfolioCompanyGet'] });

      toast.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: 'Company updated successfully',
      });
    },
    onError: () => {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to update company details',
      });
    },
  });

  const onLogoUploadError = () => {
    toast.current?.show({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to upload logo',
    });
  };

  return (
    <div
      className="flex flex-column mt-3 w-full p-3 gray-border"
      style={{
        borderRadius: '8px',
      }}
    >
      <Toast ref={toast} position="bottom-center" />
      <div>
        <h3 className="w-full text-center pb-4">Portfolio Company Settings</h3>
        <div className="flex justify-content-center">
          {selectedPortfolioCompanyDate &&
            !('error' in selectedPortfolioCompanyDate) && (
              <EditPortfolioCompany
                initialValue={portfolioCompanyPutPayloadSchema.parse({
                  ...selectedPortfolioCompanyDate,
                  gDriveFolder:
                    selectedPortfolioCompanyDate.googleWatchChannel?.folderName,
                })}
                id={selectedPortfolioCompanyDate.id}
                fundId={selectedPortfolioCompanyDate.fundId}
                onSubmit={mutation.mutate}
                isLoading={mutation.isPending}
                onLogoUploadError={onLogoUploadError}
                refetchPcDate={() => refetchPcDate()}
              />
            )}
        </div>
      </div>
    </div>
  );
}
